import { useStory } from '../contexts/StoryContext'
import './StoryEngine.css'

// Mood-based colors for illustration placeholders
const moodColors = {
  anxious: '#fde68a',
  comforted: '#c4b5fd',
  encouraged: '#fcd34d',
  inspired: '#f9a8d4',
  hopeful: '#67e8f9',
  excited: '#fdba74',
  joyful: '#86efac',
  triumphant: '#fcd34d',
  confident: '#93c5fd',
  thoughtful: '#cbd5e1',
  wise: '#a5b4fc',
  wonder: '#e9d5ff',
  enchanted: '#f0abfc',
  warm: '#fdba74',
  playful: '#f9a8d4',
  delighted: '#fde047',
  complete: '#a5b4fc',
  happy: '#86efac',
  urgent: '#fca5a5',
  tense: '#f87171',
  mysterious: '#c4b5fd',
  surprising: '#fcd34d',
  proud: '#c084fc'
}

// Scene icons for illustration placeholders
const sceneIcons = {
  'bedroom-morning': '🌅',
  'kitchen-mom': '👩‍👧',
  'kitchen-family': '👨‍👩‍👧',
  'kitchen-story': '📖',
  'bus-door': '🚌',
  'bus-inside': '💺',
  'bus-friends': '🤝',
  'bus-driver': '🚍',
  'bus-observation': '👀',
  'school-hallway': '🏫',
  'classroom-new': '✏️',
  'classroom-happy': '😊',
  'hallway-new-friend': '🤗',
  'forest-glowing-path': '✨',
  'magical-tree-door': '🚪',
  'bunny-pip': '🐰',
  'forest-gathering': '🌲',
  'magical-feast': '🎉',
  'moonlit-walk-home': '🌙',
  'mama-fox-kitchen': '🦊',
  'family-adventure': '❤️',
  'glowing-tag': '🏷️',
  'fuzzy-creatures': '☁️',
  'space-alarm': '🚨',
  'space-crack': '💫',
  'cryo-pods': '❄️',
  'computer-logs': '💻',
  'ai-interface': '🤖',
  'hero-moment': '🦸',
  'creative-fix': '🔧',
  'teamwork': '🤝'
}

function IllustrationPlaceholder({ scene, mood }) {
  const bgColor = moodColors[mood] || '#e2e8f0'
  const icon = sceneIcons[scene] || '📖'
  const sceneName = scene ? scene.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Story Scene'

  return (
    <div 
      className="illustration-placeholder"
      style={{ backgroundColor: bgColor }}
    >
      <span className="illustration-icon">{icon}</span>
      <span className="illustration-label">{sceneName}</span>
    </div>
  )
}

function StoryEngine({ onExit }) {
  const { currentStory, currentNode, storyHistory, makeChoice, goBack, exitStory } = useStory()

  if (!currentStory || !currentNode) {
    return <div className="story-engine-error">No story loaded</div>
  }

  const isEnding = !currentNode.choices || currentNode.choices.length === 0
  const canGoBack = storyHistory.length > 1

  const handleExit = () => {
    exitStory()
    onExit()
  }

  // Clean and format the text (remove any leftover markdown)
  const cleanText = (text) => {
    if (!text) return ''
    return text
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/^#+\s*/gm, '')
      .trim()
  }

  // Split text into paragraphs for better rendering
  const renderText = (text) => {
    const cleanedText = cleanText(text)
    const paragraphs = cleanedText.split('\n\n').filter(p => p.trim())
    
    if (paragraphs.length <= 1) {
      return <p>{cleanedText}</p>
    }
    
    return paragraphs.map((para, idx) => (
      <p key={idx}>{para.trim()}</p>
    ))
  }

  return (
    <div className="story-engine">
      <header className="story-header">
        <button className="back-btn" onClick={handleExit} aria-label="Exit story">
          ← Exit
        </button>
        <h1 className="story-title">{currentStory.title}</h1>
        <div className="progress">
          {storyHistory.length} / {Object.keys(currentStory.nodes).length}
        </div>
      </header>

      <main className="story-content">
        {/* Illustration - show placeholder if it's not a URL */}
        {currentNode.illustration && (
          currentNode.illustration.startsWith('http') ? (
            <div className="illustration-container">
              <img 
                src={currentNode.illustration} 
                alt="Story illustration" 
                className="illustration"
              />
            </div>
          ) : (
            <IllustrationPlaceholder 
              scene={currentNode.illustration} 
              mood={currentNode.mood}
            />
          )
        )}

        <div className="story-text">
          {renderText(currentNode.text)}
        </div>

        {/* Hints for learner mode */}
        {currentNode.hints && currentNode.hints.length > 0 && (
          <div className="hints">
            {currentNode.hints.map((hint, i) => (
              <span key={i} className="hint" title={hint.translation}>
                {hint.word} 💡
              </span>
            ))}
          </div>
        )}

        {/* Cultural note for learners */}
        {currentNode.culturalNote && (
          <div className="cultural-note">
            📚 {currentNode.culturalNote}
          </div>
        )}
      </main>

      <footer className="story-choices">
        {isEnding ? (
          <div className="ending">
            <h2>🎉 The End!</h2>
            {currentNode.achievement && (
              <div className="achievement">
                ⭐ {currentNode.achievement}
              </div>
            )}
            <div className="ending-actions">
              <button onClick={() => window.location.reload()} className="choice-btn">
                Read Again
              </button>
              <button onClick={handleExit} className="choice-btn primary">
                More Stories
              </button>
            </div>
          </div>
        ) : (
          <div className="choices">
            {currentNode.choices.map((choice, index) => (
              <button
                key={index}
                className={`choice-btn ${choice.emotion ? `emotion-${choice.emotion}` : ''}`}
                onClick={() => makeChoice(choice)}
              >
                {cleanText(choice.text)}
              </button>
            ))}
          </div>
        )}

        {canGoBack && !isEnding && (
          <button className="go-back-btn" onClick={goBack}>
            ← Go back
          </button>
        )}
      </footer>
    </div>
  )
}

export default StoryEngine
